// Navegação igual à do protótipo: uma tela por vez e, por cima, um painel (sheet).
// O botão "voltar" do celular fecha o painel ou volta para a tela anterior.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toastBus } from './toast.tsx';

export type View =
  | 'hoje' | 'agenda' | 'clientes' | 'cliente' | 'caixa' | 'mais'
  | 'pendencias' | 'servicos' | 'servico' | 'equipe' | 'parceria' | 'pagamentos' | 'regras' | 'salao' | 'horarios' | 'folgas'
  | 'acessos' | 'perfilAcesso' | 'perfil' | 'setup' | 'importar' | 'historico' | 'relatorios' | 'repasse' | 'maquininha'
  | 'mensagens' | 'lembretes' | 'meuLink' | 'espera' | 'avaliacoes'
  | 'tabela' | 'despesas' | 'vendas' | 'produtos' | 'pacotes' | 'vales' | 'fidelidade';

export type Drawer =
  | { kind: 'appt'; id: string }
  | { kind: 'novo'; date?: string; time?: string; professionalId?: string; clientId?: string; rescheduleId?: string }
  | { kind: 'comanda'; id: string }
  | { kind: 'juntas'; ids: string[] }
  | { kind: 'temHorario'; clientId?: string }
  | { kind: 'cadeira'; id: string }
  | { kind: 'fechar' }
  | { kind: 'mov'; type: 'supply' | 'withdrawal' }
  | { kind: 'abrirCaixa' }
  | { kind: 'editarAbertura' }
  | { kind: 'estorno'; orderId: string }
  | { kind: 'anamnese'; clientId: string }
  | { kind: 'editCli'; clientId?: string; name?: string; then?: 'novo' }
  | { kind: 'divida'; clientId: string }
  | { kind: 'cancelar'; id: string }
  | { kind: 'falta'; id: string }
  | { kind: 'bloqueio'; professionalId?: string | null; date?: string; time?: string }
  | { kind: 'modelos' }
  | { kind: 'novoPerfil'; fromId?: string }
  | { kind: 'membro'; id: string }
  | { kind: 'convite' }
  | { kind: 'avisos' }
  | { kind: 'retorno' }
  | { kind: 'despesa'; month?: string }
  | { kind: 'carrinho' }
  | { kind: 'produto'; id?: string }
  | { kind: 'pacote'; clientId?: string }
  | { kind: 'vale' }
  | { kind: 'juntar'; clientId: string; otherId: string }
  | { kind: 'fotos'; clientId: string; appointmentId?: string }
  | { kind: 'instalar' };

interface NavState { view: View; params: Record<string, string>; drawer: Drawer | null }

interface Nav extends NavState {
  go: (view: View, params?: Record<string, string>) => void;
  open: (d: Drawer) => void;
  close: () => void;
  back: () => void;
}

const Ctx = createContext<Nav | null>(null);

export const NAV_KEY = 'sublime-nav';
export const forgetNav = () => { try { sessionStorage.removeItem(NAV_KEY); sessionStorage.removeItem('sublime-agenda-dia'); } catch { /* sem armazenamento */ } };
// ao recarregar a página, volta para a tela em que a pessoa estava (a ficha ou o painel aberto por cima não voltam)
function loadNav(): NavState {
  try {
    const j = JSON.parse(sessionStorage.getItem(NAV_KEY) ?? 'null') as { view?: unknown; params?: unknown } | null;
    if (j && typeof j.view === 'string' && j.view !== 'mais') return { view: j.view as View, params: (j.params && typeof j.params === 'object' ? j.params : {}) as Record<string, string>, drawer: null };
  } catch { /* sem armazenamento */ }
  return { view: 'hoje', params: {}, drawer: null };
}

export function NavProvider({ children }: { children: ReactNode }) {
  const [s, set] = useState<NavState>(loadNav);
  const stack = useRef<NavState[]>([]);
  const ref = useRef(s);
  ref.current = s;

  // abrir uma tela a partir de um painel: o painel fecha e a entrada dele no histórico vira a da tela
  // (sem history.back, que chegaria depois e desfaria a navegação)
  const go = useCallback((view: View, params: Record<string, string> = {}) => {
    stack.current.push({ ...ref.current, drawer: null });
    if (ref.current.drawer && history.state?.sb === 'drawer') history.replaceState({ sb: 'view' }, '');
    else history.pushState({ sb: 'view' }, '');
    set({ view, params, drawer: null });
    window.scrollTo(0, 0);
  }, []);
  const open = useCallback((d: Drawer) => {
    if (!ref.current.drawer) history.pushState({ sb: 'drawer' }, '');
    set((x) => ({ ...x, drawer: d }));
  }, []);
  const close = useCallback(() => {
    if (ref.current.drawer && history.state?.sb === 'drawer') history.back();
    else set((x) => ({ ...x, drawer: null }));
  }, []);
  const back = useCallback(() => history.back(), []);
  useEffect(() => { toastBus.clear(); }, [s.view, s.params.id]);
  useEffect(() => { try { sessionStorage.setItem(NAV_KEY, JSON.stringify({ view: s.view, params: s.params })); } catch { /* sem armazenamento */ } }, [s.view, s.params]);

  useEffect(() => {
    const onPop = () => {
      const cur = ref.current;
      if (cur.drawer) { set({ ...cur, drawer: null }); return; }
      const prev = stack.current.pop();
      set(prev ?? { view: 'hoje', params: {}, drawer: null });
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && ref.current.drawer) close(); };
    window.addEventListener('popstate', onPop);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('popstate', onPop); window.removeEventListener('keydown', onKey); };
  }, [close]);

  useEffect(() => { document.body.classList.toggle('locked', !!s.drawer); }, [s.drawer]);

  const value = useMemo(() => ({ ...s, go, open, close, back }), [s, go, open, close, back]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useNav(): Nav {
  const n = useContext(Ctx);
  if (!n) throw new Error('NavProvider ausente');
  return n;
}
